/**
 * SEO Component
 * Dynamically updates meta tags for better SEO
 */
import { useEffect } from 'react';

interface SEOProps {
    title: string;
    description: string;
    keywords?: string;
    canonicalUrl: string;
    ogType?: string;
    ogImage?: string;
    publishDate?: string;
    modifiedDate?: string;
    author?: string;
    articleSection?: string;
    tags?: string[];
}

export default function SEO({
    title,
    description,
    keywords,
    canonicalUrl,
    ogType = 'article',
    ogImage = 'https://www.paywise365.com/logo.svg',
    publishDate,
    modifiedDate,
    author = 'PayWise365',
    articleSection,
    tags = [],
}: SEOProps) {
    useEffect(() => {
        // Update document title
        document.title = title;

        // Helper function to update or create meta tags
        const updateMetaTag = (selector: string, content: string, attribute: string = 'content') => {
            let element = document.querySelector(selector);
            if (!element) {
                element = document.createElement('meta');
                const attr = selector.match(/\[(.*?)=/)?.[1];
                const value = selector.match(/="(.*?)"/)?.[1];
                if (attr && value) {
                    element.setAttribute(attr, value);
                    document.head.appendChild(element);
                }
            }
            if (element) {
                element.setAttribute(attribute, content);
            }
        };

        // Helper function to update link tags
        const updateLinkTag = (rel: string, href: string) => {
            let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
            if (!element) {
                element = document.createElement('link');
                element.setAttribute('rel', rel);
                document.head.appendChild(element);
            }
            element.href = href;
        };

        // Update basic meta tags
        updateMetaTag('meta[name="description"]', description);
        if (keywords) {
            updateMetaTag('meta[name="keywords"]', keywords);
        }
        updateMetaTag('meta[name="author"]', author);

        // Update Open Graph tags
        updateMetaTag('meta[property="og:title"]', title);
        updateMetaTag('meta[property="og:description"]', description);
        updateMetaTag('meta[property="og:type"]', ogType);
        updateMetaTag('meta[property="og:url"]', canonicalUrl);
        updateMetaTag('meta[property="og:image"]', ogImage);

        // Update Twitter Card tags
        updateMetaTag('meta[property="twitter:title"]', title);
        updateMetaTag('meta[property="twitter:description"]', description);
        updateMetaTag('meta[property="twitter:url"]', canonicalUrl);
        updateMetaTag('meta[property="twitter:image"]', ogImage);

        // Update canonical URL
        updateLinkTag('canonical', canonicalUrl);

        // Add article-specific meta tags if dates are provided
        if (publishDate) {
            updateMetaTag('meta[property="article:published_time"]', publishDate);
        }
        if (modifiedDate) {
            updateMetaTag('meta[property="article:modified_time"]', modifiedDate);
        }
        if (author) {
            updateMetaTag('meta[property="article:author"]', author);
        }
        if (articleSection) {
            updateMetaTag('meta[property="article:section"]', articleSection);
        }

        // Add article tags
        tags.forEach((tag) => {
            updateMetaTag(`meta[property="article:tag"][content="${tag}"]`, tag);
        });

        // Add JSON-LD structured data for articles
        if (ogType === 'article' && publishDate) {
            const structuredData = {
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: title,
                description: description,
                image: ogImage,
                datePublished: publishDate,
                dateModified: modifiedDate || publishDate,
                author: {
                    '@type': 'Organization',
                    name: author,
                    url: 'https://www.paywise365.com',
                },
                publisher: {
                    '@type': 'Organization',
                    name: 'PayWise365',
                    url: 'https://www.paywise365.com',
                    logo: {
                        '@type': 'ImageObject',
                        url: 'https://www.paywise365.com/logo.svg',
                    },
                },
                mainEntityOfPage: {
                    '@type': 'WebPage',
                    '@id': canonicalUrl,
                },
            };

            // Remove existing schema if present
            const existingSchema = document.getElementById('article-schema');
            if (existingSchema) {
                existingSchema.remove();
            }

            // Add new schema
            const script = document.createElement('script');
            script.id = 'article-schema';
            script.type = 'application/ld+json';
            script.textContent = JSON.stringify(structuredData);
            document.head.appendChild(script);
        }

        // Cleanup function
        return () => {
            // Reset to default values on component unmount
            document.title = 'PayWise365 - Mortgage & Loan Calculator Suite 2026 | 按揭計算機套裝';
        };
    }, [title, description, keywords, canonicalUrl, ogType, ogImage, publishDate, modifiedDate, author, articleSection, tags]);

    // This component doesn't render anything
    return null;
}
